import {
    TRANSLATION_LIMITS
} from "mock-data/translation/languages";

import translateItems, {
    markTranslationApiUnavailable
} from "./translation-ai-client";
import {
    getCachedText,
    hashText,
    setCachedText
} from "./translation-cache";


const queue = {};
let queueTimer = null;

const warnDev = (...params) => {
    if (process.env.NODE_ENV !== "development") return;

    console.warn(...params);
};

const shouldSkipText = (text) => {
    const value = String(text || "").trim();

    if (!value) return true;
    if (value.length > TRANSLATION_LIMITS.maxTextChars) return true;
    if (/^https?:\/\//i.test(value)) return true;
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return true;
    if (/^[\d\s.,:/\\#+-]+$/.test(value)) return true;
    if (/^[A-Z0-9._-]{2,20}$/.test(value)) return true;

    return false;
};

const normalizeTranslationText = (text) => String(text || "")
    .trim()
    .replace(/\s+/g, " ");

const sourceScriptTextPattern = /[\u0400-\u04FF]/;

const hasSourceScriptText = (text) => sourceScriptTextPattern.test(String(text || ""));

const isNoopTranslation = ({
        sourceLang,
        targetLang,
        sourceText,
        translatedText
    }) => {
    return sourceLang !== targetLang &&
        normalizeTranslationText(sourceText) === normalizeTranslationText(translatedText);
};

const isGenericInputTranslation = (translatedText) => {
    const value = normalizeTranslationText(translatedText).toLowerCase();

    return [
        "translation",
        "translated text",
        "uebersetzung",
        "\u00fcbersetzung",
        "deutsche uebersetzung",
        "deutsche \u00fcbersetzung"
    ].indexOf(value) !== -1;
};

const hasUntranslatedSourceScript = ({
        targetLang,
        sourceText,
        translatedText
    }) => {
    return targetLang === "de" &&
        hasSourceScriptText(sourceText) &&
        hasSourceScriptText(translatedText);
};

const isInvalidInputTranslation = ({
        sourceLang,
        targetLang,
        sourceText,
        translatedText
    }) => {
    return isNoopTranslation({ sourceLang, targetLang, sourceText, translatedText }) ||
        isGenericInputTranslation(translatedText) ||
        hasUntranslatedSourceScript({ targetLang, sourceText, translatedText });
};

const normalizeItems = (items) => {
    const result = [];
    const usedKeys = {};
    let chars = 0;

    for (let item of items) {
        if (!item || shouldSkipText(item.text)) continue;
        if (usedKeys[item.key]) continue;

        const text = String(item.text);

        if (result.length >= TRANSLATION_LIMITS.maxBatchItems) break;
        if (chars + text.length > TRANSLATION_LIMITS.maxBatchChars) break;

        usedKeys[item.key] = true;
        chars += text.length;
        result.push({
            key  : item.key,
            text : text
        });
    }

    return result;
};

const translateBatch = async ({
        sourceLang,
        targetLang,
        namespace,
        items
    }) => {
    const normalizedItems = normalizeItems(items);

    if (!normalizedItems.length) return { items: [] };

    const result = await translateItems({
        sourceLang,
        targetLang,
        namespace,
        items: normalizedItems
    });

    return result;
};

const flushQueue = async () => {
    queueTimer = null;

    const queuedItems = Object.values(queue);

    if (!queuedItems.length) return;

    const first = queuedItems[0];
    const batch = [];
    let chars = 0;

    for (let item of queuedItems) {
        if (
            item.sourceLang !== first.sourceLang ||
            item.targetLang !== first.targetLang ||
            item.namespace !== first.namespace
        ) {
            continue;
        }

        if (batch.length >= TRANSLATION_LIMITS.maxBatchItems) break;
        if (chars + item.text.length > TRANSLATION_LIMITS.maxBatchChars) break;

        batch.push(item);
        chars += item.text.length;
        delete queue[item.cacheKey];
    }

    try {
        const result = await translateBatch({
            sourceLang  : first.sourceLang,
            targetLang  : first.targetLang,
            namespace   : first.namespace,
            items       : batch
        });

        const translatedByKey = {};

        (result.items || []).forEach((item) => {
            translatedByKey[item.key] = item.translatedText;
        });

        batch.forEach((item) => {
            const translatedText = translatedByKey[item.key] || item.text;

            if (translatedByKey[item.key]) {
                setCachedText(item.cacheParams, translatedText);
            }

            item.callbacks.forEach((callback) => callback(translatedText));
        });
    } catch (e) {
        warnDev("Translation request failed", e);

        markTranslationApiUnavailable(e);

        batch.forEach((item) => {
            item.callbacks.forEach((callback) => callback(item.text));
        });
    }

    if (Object.keys(queue).length) {
        queueTimer = setTimeout(flushQueue, 250);
    }
};

export const requestTextTranslation = ({
        sourceLang,
        targetLang,
        namespace = "data",
        context = "",
        text,
        key
    }, callback) => {
    if (!targetLang || sourceLang === targetLang || shouldSkipText(text)) {
        callback(text || "");
        return () => {};
    }

    const cacheParams = {
        sourceLang,
        targetLang,
        namespace,
        context,
        text
    };
    const cached = getCachedText(cacheParams);

    if (cached) {
        callback(cached.translatedText);
        return () => {};
    }

    const itemKey = key || `text_${ hashText(`${ sourceLang }:${ targetLang }:${ namespace }:${ context }:${ text }`) }`;
    const cacheKey = `${ sourceLang }:${ targetLang }:${ namespace }:${ key || context }:${ text }`;

    if (!queue[cacheKey]) {
        queue[cacheKey] = {
            sourceLang,
            targetLang,
            namespace,
            context,
            text: String(text || ""),
            key: itemKey,
            cacheKey,
            cacheParams,
            callbacks: []
        };
    }

    queue[cacheKey].callbacks.push(callback);

    if (!queueTimer) {
        queueTimer = setTimeout(flushQueue, 350);
    }

    return () => {
        if (!queue[cacheKey]) return;

        queue[cacheKey].callbacks = queue[cacheKey].callbacks.filter((item) => item !== callback);

        if (!queue[cacheKey].callbacks.length) {
            delete queue[cacheKey];
        }
    };
};

export const requestInputTranslation = async ({
        sourceLang,
        targetLang = "de",
        namespace = "input",
        context = "",
        text
    }) => {
    const value = String(text || "");

    if (!value.trim() || sourceLang === targetLang) return value;

    if (value.length > TRANSLATION_LIMITS.maxTextChars) {
        const error = new Error("Translation text limit exceeded");
        error.status = 413;
        throw error;
    }

    if (shouldSkipText(value)) return value;

    const cacheParams = {
        sourceLang,
        targetLang,
        namespace,
        context,
        text: value
    };
    const cached = getCachedText(cacheParams);

    if (cached && !isInvalidInputTranslation({
        sourceLang,
        targetLang,
        sourceText      : value,
        translatedText : cached.translatedText
    })) {
        return cached.translatedText;
    }

    let result;

    try {
        result = await translateBatch({
            sourceLang,
            targetLang,
            namespace,
            items: [
                {
                    key: "input",
                    text: value
                }
            ]
        });
    } catch (e) {
        markTranslationApiUnavailable(e);
        throw e;
    }

    const translatedText = (result.items || [])
        .find((item) => item.key === "input")
        ?.translatedText;

    if (!translatedText) {
        throw new Error("Input translation response is empty");
    }

    if (isInvalidInputTranslation({
        sourceLang,
        targetLang,
        sourceText      : value,
        translatedText
    })) {
        throw new Error("Input translation returned unchanged text");
    }

    setCachedText(cacheParams, translatedText);

    return translatedText;
};
